import React from 'react';

import { Select } from 'components/partials/select/select';
import Input from 'components/partials/input/input';
import IconButton from 'components/partials/button/icon-button';
import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';
import ExpanderCell from 'components/partials/table-component/expander-cell/expander-cell';

import Icons from 'assets/icons';

const commonHeaderStyles =
  'h-11 border-r-2 border-white font-semibold cursor-pointer text-xs py-0';

const controlStyles = {
  padding: '0',
  minHeight: '32px',
  height: '32px',
  fontSize: '14px',
};

interface FundsTableSettingsParams {
  onDelete: (key: string | number) => void;
  onAwardingTypeChange: (key: string | number, value: string) => void;
  onAwardAmountChange: (
    key: string | number,
    awardIndex: number,
    value: unknown
  ) => void;
  awardingTypeOptions: { value: string; label: string }[];
}

type CellRow = Record<string, unknown> & {
  isParent?: boolean;
  childCount?: number;
  name?: string;
  id?: string | number;
  _tempId?: string | number;
  parentId?: string | number;
  awardIndex?: number;
  awardingType?: string;
  previousYearAmount?: number;
  previousYearRecipients?: number;
  currentYearAmount?: number;
};

interface CellInfo {
  row: {
    original: CellRow;
    getCanExpand: () => boolean;
  };
  renderValue: () => unknown;
}

export const getFundsTableSettings = ({
  onDelete,
  onAwardingTypeChange,
  onAwardAmountChange,
  awardingTypeOptions,
}: FundsTableSettingsParams): unknown[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: () => <span>FUND NAME</span>,
    cell: (info: CellInfo) => {
      const row = info.row.original;

      if (row.isParent) {
        const childCount = (row.childCount as number) || 0;
        const name = info.renderValue();
        const canExpand = info.row.getCanExpand();

        return (
          <span className="flex items-center gap-2 font-semibold text-gray-700">
            <TruncatedCell value={name as never} />
            {childCount > 0 && (
              <span className="whitespace-nowrap">({childCount})</span>
            )}
            {canExpand && <ExpanderCell row={info.row as never} />}
          </span>
        );
      }

      return <span className="font-normal text-gray-500">{row.name}</span>;
    },
    headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
    className: 'text-left px-2.5 py-2 w-[47%] max-w-0',
    enableSorting: true,
    sortingFn: 'alphanumeric',
  },
  {
    id: 'previousYearAmount',
    header: () => <span>2025</span>,
    cell: (info: CellInfo) => {
      const row = info.row.original;

      if (row.isParent) {
        return <span className="text-gray-400 text-sm">-</span>;
      }

      return (
        <span className="flex items-center gap-1.5">
          <span>$</span>
          <span>{row.previousYearAmount?.toLocaleString()}</span>
        </span>
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[9%] px-2.5 py-2',
    enableSorting: false,
  },
  {
    id: 'totalRecipients',
    header: () => <span>TOTAL RECIPIENTS</span>,
    cell: (info: CellInfo) => {
      const row = info.row.original;

      if (row.isParent) {
        return <span className="text-gray-400 text-sm">-</span>;
      }

      return row.previousYearRecipients;
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[13%] px-2.5 py-2',
    enableSorting: false,
  },
  {
    id: 'currentYearAmount',
    header: () => <span>2026</span>,
    cell: (info: CellInfo) => {
      const row = info.row.original;

      if (row.isParent) {
        return <span className="text-gray-400 text-sm">-</span>;
      }

      const fundKey = row.parentId;

      return (
        <div className="flex justify-center px-0.5 py-1">
          <Input
            type="text"
            value={
              row.currentYearAmount != null
                ? `$ ${Number(row.currentYearAmount).toLocaleString()}`
                : ''
            }
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9.]/g, '');

              if (fundKey == null) return;

              onAwardAmountChange(
                fundKey,
                row.awardIndex ?? 0,
                raw ? Number.parseFloat(raw) : null
              );
            }}
            placeholder="$ 0"
            inputCustomClass="h-8 !py-1 !px-2.5 text-sm"
            inputWrapperCustomClass="!m-0"
            data-testid={`fund-amount-input-${row.id}`}
          />
        </div>
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[11%] px-2.5',
    enableSorting: false,
  },
  {
    id: 'awardingType',
    accessorKey: 'awardingType',
    header: () => <span>AWARDING LOGIC</span>,
    cell: (info: CellInfo) => {
      const row = info.row.original;
      const isParent = row.isParent;
      const options = awardingTypeOptions || [];

      const selectedOption = options.find(
        (opt) => opt.value === row.awardingType
      );

      return (
        <div className="px-0.5 py-1">
          <Select
            options={options}
            value={selectedOption}
            onChange={(option) => {
              if (!isParent) return;

              const fundKey = row.id || row._tempId;

              if (fundKey == null) return;

              if (option && !Array.isArray(option)) {
                onAwardingTypeChange(fundKey, String(option.value));
              }
            }}
            selectClass="w-full"
            placeholder="Select..."
            isSearchable={false}
            disabled={!isParent}
            disabledBgColor="#F2F2F2"
            extraBasicStyles={controlStyles}
            extraStyles={() => ({
              valueContainer: (provided: Record<string, unknown>) => ({
                ...provided,
                height: '30px',
                padding: '0 10px',
                display: 'flex',
                alignItems: 'center',
              }),
              singleValue: (provided: Record<string, unknown>) => ({
                ...provided,
                margin: 0,
                lineHeight: '20px',
              }),
              indicatorsContainer: (provided: Record<string, unknown>) => ({
                ...provided,
                height: '30px',
                alignItems: 'center',
              }),
              dropdownIndicator: (provided: Record<string, unknown>) => ({
                ...provided,
                padding: '0 10px 0 6px',
              }),
              input: (provided: Record<string, unknown>) => ({
                ...provided,
                margin: 0,
                padding: 0,
              }),
              menuPortal: (provided: Record<string, unknown>) => ({
                ...provided,
                zIndex: 9999,
              }),
            })}
            menuPortalTarget={document.body}
            data-testid={`fund-awarding-select-${row.id}`}
          />
        </div>
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[16%] px-2.5',
    enableSorting: false,
  },
  {
    id: 'actions',
    header: () => null,
    cell: (info: CellInfo) => {
      const row = info.row.original;

      if (!row.isParent) return null;

      return (
        <IconButton
          variant="reserve-text"
          size="xs"
          icon={<Icons.Delete />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const key = row.id || row._tempId;

            if (key != null) onDelete(key);
          }}
          className="mx-auto text-navy-500 hover:!bg-navy-50 hover:!text-navy-700 focus-visible:!ring-navy-300"
          aria-label={`Remove ${row.name}`}
          data-testid={`fund-remove-button-${row.id || row._tempId}`}
        />
      );
    },
    headerClassName: 'rounded-tr-md rounded-br-md text-xs h-8 py-0',
    className: 'w-[4%] px-2',
    enableSorting: false,
  },
];
